import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useTodoLists, useTodoShared } from '../hooks';
import { ListCard, CreateListDialog } from '../components';

export function MyListsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lists, loading, error } = useTodoLists();
  const { sharedLists } = useTodoShared();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const handleCreate = async (input: { name: string; description?: string }) => {
    const newList = await api.createTodoList(input);
    await queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.my() });
    setCreateDialogOpen(false);
    navigate(`/a/arche_todo_lists/${newList.listId}`);
  };

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_todo_lists" title="Todo Lists" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_todo_lists" title="Todo Lists">
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New List
        </Button>
      </ArcheHeader>
      <div className="max-w-5xl mx-auto p-6 space-y-6 w-full">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : lists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ListTodo className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground mb-4">No todo lists yet</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first list
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lists.map((list) => (
              <ListCard
                key={list.listId}
                list={list}
                onClick={() => navigate(`/a/arche_todo_lists/${list.listId}`)}
              />
            ))}
          </div>
        )}

        {sharedLists.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-medium text-muted-foreground">Shared with me</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedLists.map((sl) => (
                <div
                  key={sl.listId}
                  className="border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/a/arche_todo_lists/${sl.listId}`)}
                >
                  <h3 className="font-medium truncate">{sl.name}</h3>
                  {sl.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {sl.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {sl.collaboratorRole === 'editor' ? 'Editor' : 'Viewer'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <CreateListDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onCreate={handleCreate}
        />
      </div>
    </div>
  );
}
